import "./index.css";
import { BellIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { useBreakpoints } from "@/hooks/useBreakpoints";
import {
  Badge,
  ConfirmDialog,
  Drawer,
  IconButton,
  Popover,
  Tooltip,
  useBoolean,
} from "@/ui-kit";
import { serverErrorHandler } from "@/utils/serverErrorHandler";
import { notificationsCenter, useNotifications } from "../../model";
import { HeaderMenu } from "../HeaderMenu";
import { NotificationsPanel } from "../NotificationsPanel";

const deleteAllNotifications = () =>
  notificationsCenter.removeAll().catch(serverErrorHandler());

export const NotificationButton = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { tenant } = useParams<{ tenant: string }>();
  const { isMobile } = useBreakpoints();
  const count = useNotifications((s) => s.unreadCount);
  const [open, setOpen] = useState(false);

  const {
    value: showConfirmDeleteAll,
    setTrue: openConfirmDeleteAll,
    setFalse: dismissConfirmDeleteAll,
  } = useBoolean(false);

  // The live counter follows the tenant in the URL
  useEffect(() => {
    if (!tenant) return;

    notificationsCenter.start(tenant);
    return () => notificationsCenter.stop();
  }, [tenant]);

  const toggleOpen = useCallback(() => {
    setOpen((value) => !value);
  }, []);

  const handleNavigate = useCallback(
    (path: string) => {
      setOpen(false);
      navigate(path);
    },
    [navigate],
  );

  const handleDeleteAll = useCallback(() => {
    setOpen(false);
    openConfirmDeleteAll();
  }, [openConfirmDeleteAll]);

  if (!tenant) {
    return null;
  }

  const bell = (
    <Badge
      className="notifications-badge"
      size="md"
      hide={!count}
      anchor={<BellIcon />}
      content={count}
    />
  );

  const panel = (
    <NotificationsPanel
      isMobile={isMobile}
      onNavigate={handleNavigate}
      onDeleteAll={handleDeleteAll}
    />
  );

  return (
    <>
      {isMobile ? (
        <>
          <IconButton
            variant="ghost"
            color="neutral"
            size="md"
            content={bell}
            onClick={toggleOpen}
            aria-label={t("label.notifications")}
          />
          {open && (
            <Drawer
              open={true}
              onOpenChange={toggleOpen}
              className="notifications-drawer"
            >
              <Drawer.Header title={t("label.notifications")}>
                <HeaderMenu onDeleteAll={handleDeleteAll} />
              </Drawer.Header>
              <Drawer.Body>{panel}</Drawer.Body>
            </Drawer>
          )}
        </>
      ) : (
        <Tooltip content={t("label.notifications")} placement="bottom-end">
          <Popover
            open={open}
            onOpenChange={setOpen}
            content={panel}
            contentClassName="notifications-dropdown-content"
            placement="bottom-end"
            maxWidth={420}
          >
            <IconButton
              variant="ghost"
              color="neutral"
              size="md"
              content={bell}
              aria-label={t("label.notifications")}
            />
          </Popover>
        </Tooltip>
      )}
      {showConfirmDeleteAll && (
        <ConfirmDialog
          onDismiss={dismissConfirmDeleteAll}
          title={t("confirm.delete_title")}
          subtitle={t("confirm.deleteAllNotificationsSubtitle")}
          submitText={t("action.deleteAll")}
          submitColor="danger"
          submitAction={deleteAllNotifications}
        />
      )}
    </>
  );
};
